/**
 * CategorySource abstraction (DIP): UI depends on this interface, not on concrete data source.
 * Default implementation derives categories from current products in-memory.
 * An API-backed adapter can be plugged later without touching UI components.
 */

export interface CategorySource {
  list(search?: string): Promise<string[]>;
  create(name: string): Promise<string>;
  rename(oldName: string, newName: string): Promise<void>;
}

/**
 * Factory for a derived (in-memory) CategorySource.
 * It reads current products and writes updates via provided closures, enabling mass-rename in memory.
 */
export function createDerivedCategoriesSource(opts: {
  getProducts: () => Array<{ categoria?: string | null }>;
  setProducts: (updater: (prev: any[]) => any[]) => void;
  getExtras: () => string[];
  setExtras: (updater: (prev: string[]) => string[]) => void;
}): CategorySource {
  const norm = (v: string) => String(v || '').trim();

  const list: CategorySource['list'] = async (search) => {
    const prods = opts.getProducts() || [];
    const extras = opts.getExtras() || [];
    const set = new Set<string>();
    for (const p of prods) {
      const c = norm(String(p?.categoria ?? ''));
      if (c) set.add(c);
    }
    for (const e of extras) {
      const c = norm(e);
      if (c) set.add(c);
    }
    let arr = Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const q = norm(String(search || ''));
    if (q) arr = arr.filter(x => x.toLowerCase().includes(q.toLowerCase()));
    return arr;
  };

  const create: CategorySource['create'] = async (name) => {
    const n = norm(name);
    if (!n) throw new Error('Invalid category name');
    opts.setExtras((prev) => {
      const s = new Set(prev || []);
      s.add(n);
      return Array.from(s);
    });
    return n;
  };

  const rename: CategorySource['rename'] = async (oldName, newName) => {
    const o = norm(oldName);
    const n = norm(newName);
    if (!o || !n) throw new Error('Invalid rename values');
    // Update across all loaded products in-memory
    opts.setProducts((prev) => {
      return (prev || []).map((p: any) => {
        const pc = norm(String(p?.categoria ?? ''));
        if (pc && pc.toLowerCase() === o.toLowerCase()) {
          return { ...p, categoria: n };
        }
        return p;
      });
    });
    // Update extras list as well
    opts.setExtras((prev) => {
      const arr = (prev || []).slice();
      const idx = arr.findIndex((x) => norm(x).toLowerCase() === o.toLowerCase());
      if (idx >= 0) arr[idx] = n;
      if (!arr.some(x => norm(x).toLowerCase() === n.toLowerCase())) arr.push(n);
      return Array.from(new Set(arr));
    });
  };

  return { list, create, rename };
}

/**
 * API-backed scaffold (optional). Not used by default.
 * You can wire it by importing categoryService and providing comercioKey context.
 */
export function createApiCategoriesSource(opts: {
  comercioKey: string;
  categoryApi: {
    listCategories: (ck: string, search?: string) => Promise<string[]>;
    createCategory: (ck: string, name: string) => Promise<string>;
    renameCategory: (ck: string, oldName: string, newName: string) => Promise<void>;
  };
}): CategorySource {
  const list: CategorySource['list'] = async (search) => opts.categoryApi.listCategories(opts.comercioKey, search);
  const create: CategorySource['create'] = async (name) => opts.categoryApi.createCategory(opts.comercioKey, name);
  const rename: CategorySource['rename'] = async (oldName, newName) => opts.categoryApi.renameCategory(opts.comercioKey, oldName, newName);
  return { list, create, rename };
}